import { Box, Stack, Text } from '@mantine/core'
import { memo, useEffect, useRef, useState } from 'react'
import {
  measureInputToPresentLatency,
  shouldTrackInputLatencyEvent,
} from './measureInputLatency'

const METRIC_SAMPLE_MS = 500

const metricTextProps = {
  c: 'rgba(242, 232, 217, 0.72)',
  ff: 'monospace',
  fz: 32,
  lh: 1,
} as const

function useFpsCounter() {
  const [fps, setFps] = useState(0)

  useEffect(() => {
    let frameCount = 0
    let sampleStart = performance.now()
    let raf = 0

    const tick = (now: number) => {
      frameCount += 1
      const elapsed = now - sampleStart
      if (elapsed >= METRIC_SAMPLE_MS) {
        setFps(Math.round((frameCount * 1000) / elapsed))
        frameCount = 0
        sampleStart = now
      }
      raf = requestAnimationFrame(tick)
    }

    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [])

  return fps
}

type LatencySample = {
  at: number
  ms: number
}

function useInputLatencyTracker() {
  const [latencyMs, setLatencyMs] = useState(0)
  const samplesRef = useRef<LatencySample[]>([])
  const pendingMeasurementsRef = useRef(0)

  useEffect(() => {
    const recordSample = (ms: number) => {
      const now = performance.now()
      const samples = samplesRef.current
      samples.push({ at: now, ms })

      const cutoff = now - METRIC_SAMPLE_MS
      while (samples.length > 0 && samples[0].at < cutoff) {
        samples.shift()
      }

      if (samples.length === 0) {
        return
      }

      const average = samples.reduce((sum, sample) => sum + sample.ms, 0) / samples.length
      setLatencyMs(Math.round(average))
    }

    const onInput = (event: PointerEvent | KeyboardEvent) => {
      if (!shouldTrackInputLatencyEvent(event)) {
        return
      }

      pendingMeasurementsRef.current += 1
      measureInputToPresentLatency(ms => {
        pendingMeasurementsRef.current -= 1
        recordSample(ms)
      })
    }

    window.addEventListener('pointerdown', onInput, { capture: true })
    window.addEventListener('keydown', onInput, { capture: true })

    return () => {
      window.removeEventListener('pointerdown', onInput, { capture: true })
      window.removeEventListener('keydown', onInput, { capture: true })
    }
  }, [])

  return latencyMs
}

export const PerformanceOverlay = memo(function PerformanceOverlay() {
  const fps = useFpsCounter()
  const inputLatencyMs = useInputLatencyTracker()

  return (
    <Box
      bottom={16}
      left={16}
      pos="fixed"
      style={{ pointerEvents: 'none', zIndex: 50 }}
    >
      <Stack gap={4}>
        <Text {...metricTextProps} fz={32}>{fps}</Text>
        <Text {...metricTextProps} fz={24}>{inputLatencyMs} ms</Text>
      </Stack>
    </Box>
  )
})
